import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, AuthState } from '@/entities/user/types';
import type {
  LoginCredentials,
  RegisterCredentials,
} from '@/entities/auth/types';
import authAPI from './authAPI';

const initialState: AuthState = {
  user: null,
  profile: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  skipLayoutRefetch: false,
};

export const signInWithEmail = createAsyncThunk(
  'auth/signInWithEmail',
  async (
    { credentials }: { credentials: LoginCredentials },
    { rejectWithValue }
  ) => {
    try {
      const result = await authAPI.signInWithEmail(credentials);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign in');
    }
  }
);

export const signUpWithEmail = createAsyncThunk(
  'auth/signUpWithEmail',
  async (
    {
      credentials,
      signature,
    }: { credentials: RegisterCredentials; signature?: string },
    { rejectWithValue }
  ) => {
    try {
      return await authAPI.signUpWithEmail(credentials, signature);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign up');
    }
  }
);

export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      return await authAPI.signInWithGoogle();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign in with Google');
    }
  }
);

export const registerWithGoogle = createAsyncThunk(
  'auth/registerWithGoogle',
  async (signature: string, { rejectWithValue }) => {
    try {
      return await authAPI.registerWithGoogle(signature);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign up with Google');
    }
  }
);

export const sendPasswordReset = createAsyncThunk(
  'auth/sendPasswordReset',
  async (email: string, { rejectWithValue }) => {
    try {
      await authAPI.sendPasswordReset(email);
      return email;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to send password reset email'
      );
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.signOut();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign out');
    }
  }
);

export const sendEmailVerification = createAsyncThunk(
  'auth/sendEmailVerification',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.sendEmailVerification();
      return 'Email verification sent successfully';
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to send email verification'
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      return await authAPI.updateUserProfile(userData);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user profile');
    }
  }
);

export const updateUserImage = createAsyncThunk(
  'auth/updateUserImage',
  async (
    { imageUrl, userId }: { imageUrl: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      await authAPI.updateUserImage(imageUrl, userId);
      return imageUrl;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user image');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.error = null;
    },
    clearError: state => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetAuthState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(signInWithEmail.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.skipLayoutRefetch = true;
      })
      .addCase(signInWithEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.skipLayoutRefetch = false;
      })
      .addCase(signInWithEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.skipLayoutRefetch = false;
      })
      .addCase(signUpWithEmail.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.skipLayoutRefetch = true;
      })
      .addCase(signUpWithEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.skipLayoutRefetch = false;
      })
      .addCase(signUpWithEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.skipLayoutRefetch = false;
      })
      .addCase(signInWithGoogle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.skipLayoutRefetch = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.skipLayoutRefetch = false;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.skipLayoutRefetch = false;
      })
      .addCase(registerWithGoogle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.skipLayoutRefetch = true;
      })
      .addCase(registerWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.skipLayoutRefetch = false;
      })
      .addCase(registerWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.skipLayoutRefetch = false;
      })
      .addCase(sendPasswordReset.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendPasswordReset.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendPasswordReset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(signOut.pending, state => {
        state.isLoading = true;
      })
      .addCase(signOut.fulfilled, () => initialState)
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(sendEmailVerification.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendEmailVerification.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendEmailVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = { ...state.user, ...action.payload } as User;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserImage.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserImage.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.photoURL = action.payload;
        }
        state.error = null;
      })
      .addCase(updateUserImage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearError, setLoading, resetAuthState } =
  authSlice.actions;
export default authSlice.reducer;
